import moment from 'moment'
import type { Moment } from 'moment'

import { ActivityModel } from '@/modules/drivers/types/activities'

const LABEL_WIDTH = 40
const DAY_HOURS = 24

export const getGridIntervalHours = (width: number): number => {
  if (!width || width <= 0) return 1
  let count = width / LABEL_WIDTH
  if (count > DAY_HOURS) count = DAY_HOURS
  return Math.max(1, Math.ceil(DAY_HOURS / count))
}

export const fillActivityGaps = (
  activities: ReadonlyArray<ActivityModel>,
  startTime: Moment,
  days: number,
): ActivityModel[] => {
  const dayStart = moment(startTime)
  const ds = dayStart.valueOf()
  const de = dayStart
    .clone()
    .add(days || 1, 'days')
    .valueOf()

  const items = activities
    .map((a) => ({
      s: moment(a.startTime).valueOf(),
      e: moment(a.endTime ?? a.startTime).valueOf(),
    }))
    .filter(
      (x) => !isNaN(x.s) && !isNaN(x.e) && x.e > x.s && x.e > ds && x.s < de,
    )
    .sort((x, y) => x.s - y.s)

  if (items.length === 0) return activities as ActivityModel[]

  const gaps: { s: number; e: number }[] = []
  let cursor = ds
  for (const x of items) {
    const segStart = Math.max(x.s, ds)
    if (segStart > cursor) gaps.push({ s: cursor, e: segStart })
    cursor = Math.max(cursor, Math.min(x.e, de))
  }
  if (cursor < de) gaps.push({ s: cursor, e: de })

  if (gaps.length === 0) return activities as ActivityModel[]

  const unknowns = gaps.map(
    (g, i) =>
      ({
        id: -100000 - i,
        type: 4,
        startTime: moment(g.s),
        endTime: moment(g.e),
        slot: 0,
        isCrew: false,
        isOutOfScope: false,
        isFerryCrossing: false,
        isManualEntry: false,
        isModifiedByUser: false,
        isVehicleSource: false,
        driverTachographChartId: null,
      }) as unknown as ActivityModel,
  )

  return [...activities, ...unknowns]
}

const overlapSeconds = (
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): number => {
  const s = Math.max(aStart, bStart)
  const e = Math.min(aEnd, bEnd)
  return e > s ? (e - s) / 1000 : 0
}

export const aggregateActivitiesToBuckets = (
  activities: ReadonlyArray<ActivityModel>,
  startTime: Moment,
  days: number,
  bucketHours: number,
): ActivityModel[] => {
  const dayStart = moment(startTime)
  const totalHours = (days || 1) * DAY_HOURS

  const spans = activities
    .map((a) => ({
      type: a.type,
      s: moment(a.startTime).valueOf(),
      e: moment(a.endTime ?? a.startTime).valueOf(),
    }))
    .filter((a) => !isNaN(a.s) && !isNaN(a.e) && a.e > a.s)

  const result: ActivityModel[] = []

  for (let h = 0; h < totalHours; h += bucketHours) {
    const bStart = dayStart.clone().add(h, 'hours')
    const bEnd = dayStart.clone().add(h + bucketHours, 'hours')
    const bs = bStart.valueOf()
    const be = bEnd.valueOf()

    const byType = new Map<number, number>()
    for (const sp of spans) {
      const ov = overlapSeconds(sp.s, sp.e, bs, be)
      if (ov > 0) byType.set(sp.type, (byType.get(sp.type) || 0) + ov)
    }
    if (byType.size === 0) continue

    let dominant = -1
    let best = -1
    byType.forEach((secs, type) => {
      if (secs > best) {
        best = secs
        dominant = type
      }
    })

    result.push({
      id: -(result.length + 1),
      type: dominant,
      startTime: bStart,
      endTime: bEnd,
      slot: 0,
      isCrew: false,
      isOutOfScope: false,
      isFerryCrossing: false,
      isManualEntry: false,
      isModifiedByUser: false,
      isVehicleSource: false,
      driverTachographChartId: null,
    } as unknown as ActivityModel)
  }

  return result
}
